import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Bolt as BoltIcon,
  Apps as AppsIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import DataTable from '../../common/DataTable';
import SectionCard from '../shared/SectionCard';
import { adminDashboardService } from '../../../services/dashboard';
import ApplicationDrilldownDialog from './ApplicationDrilldownDialog';

const columns = [
  { field: 'businessName', headerName: 'Business', sortable: true },
  { field: 'apps', headerName: 'Apps', align: 'right', sortable: true },
  { field: 'users', headerName: 'Users', align: 'right', format: (v) => v?.toLocaleString(), sortable: true },
  { field: 'authRequests', headerName: 'Auth Requests', align: 'right', format: (v) => v?.toLocaleString(), sortable: true },
  { field: 'successRate', headerName: 'Success Rate', align: 'right', format: (v) => `${v}%`, sortable: true },
];

const BusinessDrilldownDialog = ({ businessId, onClose, onOpenApp }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    adminDashboardService
      .getBusinessDrilldown(businessId)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [businessId]);

  const statusColor = (status) =>
    ({ ACTIVE: 'success', BLOCKED: 'error', INACTIVE: 'warning' }[status] || 'default');

  const statCards = data
    ? [
        { label: 'Subscribers', value: data.overview.subscribers.value.toLocaleString(), icon: <PeopleIcon fontSize="small" />, color: '#2563eb' },
        { label: 'Auth Requests', value: data.overview.authRequests.value.toLocaleString(), icon: <BoltIcon fontSize="small" />, color: '#f59e0b' },
        { label: 'Success Rate', value: `${data.overview.successRate.value}%`, icon: <CheckCircleIcon fontSize="small" />, color: '#16a34a' },
      ]
    : [];

  return (
    <Dialog open={Boolean(businessId)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: loading || error ? 2 : 1 }}>
        <Avatar sx={{ bgcolor: '#eef2ff', color: '#4338ca', width: 40, height: 40 }}>
          <BusinessIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {data?.business?.businessName || 'Business details'}
          </Typography>
          {data && (
            <Chip
              label={data.business.accountStatus}
              size="small"
              color={statusColor(data.business.accountStatus)}
              sx={{ mt: 0.5, fontWeight: 600, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {statCards.map((s) => (
                <Grid item xs={4} key={s.label}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2, textAlign: 'center', height: '100%' }}
                  >
                    <Box sx={{ color: s.color, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{s.icon}</Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AppsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Applications
              </Typography>
              <Chip label={data.applications.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Stack>

            {data.applications.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="text.secondary">
                  No applications for this business yet.
                </Typography>
              </Paper>
            ) : (
              <List dense disablePadding sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
                {data.applications.map((app) => (
                  <ListItemButton
                    key={app.applicationId}
                    onClick={() => onOpenApp(app.applicationId)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#f1f5f9', color: '#475569' }}>
                        <AppsIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={app.appName}
                      secondary={`${app.users} users · ${app.successRate}% success`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                    <Chip label={app.status} size="small" color={statusColor(app.status)} sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const BusinessRankingSection = ({ reloadKey }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedAppId, setSelectedAppId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminDashboardService
      .getTopBusinesses('30d', page + 1, rowsPerPage)
      .then((res) => {
        setRows(res.data.map((b) => ({ ...b, id: b.businessId })));
        setTotal(res.total);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, reloadKey]);

  return (
    <SectionCard
      title="Top Businesses"
      loading={loading}
      error={error}
      isEmpty={!loading && !error && rows.length === 0}
      minHeight={340}
    >
      <Box sx={{ height: 340, display: 'flex', flexDirection: 'column' }}>
        <DataTable
          columns={columns}
          data={rows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={total}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onView={(row) => setSelectedBusinessId(row.businessId)}
        />
      </Box>
      <BusinessDrilldownDialog
        businessId={selectedBusinessId}
        onClose={() => setSelectedBusinessId(null)}
        onOpenApp={(appId) => setSelectedAppId(appId)}
      />
      <ApplicationDrilldownDialog appId={selectedAppId} onClose={() => setSelectedAppId(null)} />
    </SectionCard>
  );
};

export default BusinessRankingSection;
